import React, { useState, useEffect } from "react";
import "./Home.css";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Grid,
  Card,
  CardActions,
  CardContent,
  Box, // Used for layout and spacing
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles"; // Import useTheme
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import AddIngredient from "./AddIngredientModal";
import EditIngredient from "./EditIngredientModal";
import { getCurrentRecipe } from "../actions/recipes";
import AddIcon from "@mui/icons-material/Add";
import HomeIcon from "@mui/icons-material/Home";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import MenuIcon from "@mui/icons-material/Menu";

// Removed: import { makeStyles } from "@mui/styles";
// Removed: useStyles definition

function Ingredients() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { recipeId } = useParams();

  // Modal states
  const [open, setOpen] = useState(false);
  const [openForEdit, setOpenForEdit] = useState(false);
  const [currentIngredient, setCurrentIngredient] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);

  const theme = useTheme(); // Access the theme for spacing
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // --- Data Fetching ---
  useEffect(() => {
    // Dispatch getCurrentRecipe when component mounts or recipeId changes
    dispatch(getCurrentRecipe(recipeId));
  }, [dispatch, recipeId]); // Added dependencies for correctness

  // Get the ingredients from Redux state (use empty array as fallback)
  const ingredients =
    useSelector((state) => state.recipes.currentRecipe.ingredients) || [];

  // --- Handlers ---
  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleEditClose = () => setOpenForEdit(false);

  const handleClickEditOpen = (tile) => {
    setCurrentIngredient(tile);
    setOpenForEdit(true);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // --- Styling Objects (Replaces makeStyles) ---
  const headerContainerSx = {
    flexGrow: 1,
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    alignItems: "center",
  };

  const navButtonContainerSx = {
    display: "flex",
    gap: theme.spacing(1), // Space between buttons
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  };

  const cardSx = {
    width: "100%",
    minHeight: 120,
    alignSelf: "center",
    display: "flex",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[3],
  };

  return (
    <React.Fragment>
      <AppBar position="static">
        <Toolbar sx={headerContainerSx}>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            component="div"
            sx={{ fontWeight: 600 }}
          >
            Recipe Ingredients
          </Typography>
          <Box sx={navButtonContainerSx}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<RestaurantIcon />}
              onClick={() => navigate("/specials")}
              sx={{ color: "#fff" }}
            >
              Specials
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={handleClickOpen}
              sx={{ color: "#fff" }}
            >
              Add Ingredient
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<HomeIcon />}
              onClick={() => navigate("/")}
            >
              Home
            </Button>
          </Box>
          {isMobile && (
            <IconButton edge="end" color="inherit" onClick={handleMenuOpen}>
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            navigate("/specials");
            handleMenuClose();
          }}
        >
          <RestaurantIcon sx={{ mr: 1 }} />
          Specials
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClickOpen();
            handleMenuClose();
          }}
        >
          <AddIcon sx={{ mr: 1 }} />
          Add Ingredient
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate("/");
            handleMenuClose();
          }}
        >
          <HomeIcon sx={{ mr: 1 }} />
          Home
        </MenuItem>
      </Menu>

      <AddIngredient open={open} onClose={handleClose} />
      <EditIngredient
        currentIngredient={currentIngredient}
        open={openForEdit}
        onClose={handleEditClose}
      />

      <Box sx={{ padding: theme.spacing(4) }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
          Ingredients List
        </Typography>

        <Grid container spacing={3} justifyContent="center">
          {ingredients.length === 0 ? (
            <Typography
              variant="subtitle1"
              color="textSecondary"
              sx={{ mt: 5 }}
            >
              No ingredients added yet. Use the "Add Ingredient" button above.
            </Typography>
          ) : (
            ingredients.map((tile, index) => (
              <Grid item xs={12} md={6} lg={4} key={tile.uuid || index}>
                <Card sx={cardSx}>
                  <CardContent sx={{ flexGrow: 1, p: 2 }}>
                    <Typography
                      gutterBottom
                      variant="h6"
                      component="h2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                      }}
                    >
                      {tile.name}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="textPrimary"
                      component="p"
                      sx={{ mt: 1 }}
                    >
                      **Amount:** {tile.amount}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="textPrimary"
                      component="p"
                    >
                      **Measurement:** {tile.measurement}
                    </Typography>
                  </CardContent>

                  <CardActions
                    sx={{
                      borderLeft: `1px solid ${theme.palette.divider}`,
                      p: 1,
                      minWidth: "100px",
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      onClick={() => handleClickEditOpen(tile)}
                      size="small"
                      color="primary"
                      sx={{ fontWeight: "bold" }}
                    >
                      Edit
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      </Box>
    </React.Fragment>
  );
}

export default Ingredients;
